import type {
  AnalysisStyleNote,
  AnalysisStyleReading,
  NoyauAnalysis,
} from '../schemas/analysis.js';
import { NOYAU_CODES, type NoyauCode } from '../schemas/noyau.js';
import { candidateNote, eliminatedNote } from './analyst-notes.js';
import { rankAnalyzedNoyaux } from './ranking.js';

/**
 * SEVERAL READINGS OF THE STYLE (analyste.md §7.6, §11.4, issue #229): the pure
 * rules that turn the readings of the second pass into the style a candidate is
 * noted on, and what a trial would have said with fewer of them.
 *
 * ## The rule that combines (§7.6, L2, L4, L5)
 *
 * The style of a candidate is the MEAN of the notes of the readings that rendered,
 * rounded to the nearest integer, a half upwards; a reading that failed does not
 * count. Its passages, its discarded passages and its sentence are those of the
 * reading CLOSEST to that style - measured on the integer, not on the exact mean:
 * with two readings both are always at the same distance of their exact mean, and
 * the oldest would win every time -, the oldest reading when two are as close.
 * It is chosen candidate by candidate, so the sentence of one noyau may come from
 * the third reading and the sentence of another from the second.
 *
 * ## One reading is the analyst of before
 *
 * At one reading (`styleRuns` 1, the default) nothing is combined: the notes are the
 * reading's own, `reading` stays null and `readings` stays empty (Y1).
 */

/** The note one reading gave a candidate. */
export type StyleRunScore = { readonly run: number; readonly score: number };

/**
 * What the readings of one candidate come to: the combined style, the number of the
 * reading that carries it, and the other readings as close as it (the tie that the
 * oldest won) - what the parcours says « à égalité avec la lecture 3 » from.
 */
export type CombinedStyleScore = {
  readonly score: number;
  readonly reading: number;
  readonly tiedReadings: readonly number[];
};

/**
 * The MEAN of the notes, rounded to the nearest whole, a half upwards (§7.6, L2) -
 * integer arithmetic, so 80.5 is 81 on every machine. Null without a note.
 */
export function meanStyleScore(scores: readonly number[]): number | null {
  if (scores.length === 0) return null;
  const sum = scores.reduce((total, score) => total + score, 0);
  return Math.floor((2 * sum + scores.length) / (2 * scores.length));
}

/**
 * The combined style of ONE candidate and the reading that carries it (§7.6, L2, L5):
 * the mean of the notes, rounded, and the reading closest to it, the oldest when two
 * are as close. Null without a reading.
 *
 * 79, 82 and 81 give 81 and the reading 3; 79 and 81 give 80 and the reading 1; 79
 * and 82 give 81 and the reading 2 (80.5 rounds to 81, and the reading 2 is one away
 * where the reading 1 is two).
 */
export function combinedStyleScore(scores: readonly StyleRunScore[]): CombinedStyleScore | null {
  const combined = meanStyleScore(scores.map((entry) => entry.score));
  if (combined === null) return null;
  const byAge = [...scores].sort((a, b) => a.run - b.run);
  const distance = (entry: StyleRunScore): number => Math.abs(entry.score - combined);
  const closest = Math.min(...byAge.map(distance));
  const nearest = byAge.filter((entry) => distance(entry) === closest);
  const carrier = nearest[0];
  if (carrier === undefined) return null;
  return {
    score: combined,
    reading: carrier.run,
    tiedReadings: nearest.slice(1).map((entry) => entry.run),
  };
}

/** The second pass once its readings are combined: the style of each candidate, and the readings that gave it. */
export type CombinedStyle =
  | {
      readonly state: 'rendered';
      readonly notes: readonly AnalysisStyleNote[];
      readonly readings: readonly AnalysisStyleReading[];
    }
  | { readonly state: 'absent'; readonly readings: readonly AnalysisStyleReading[] };

/**
 * COMBINE the readings of the second pass (§7.6): `readings` is one entry per
 * reading run, rendered with its checked notes or failed, in any order.
 *
 * `styleRuns` is the number the section asked for. At 1 the single reading is the
 * style as it always was, `readings` left empty and every `reading` null (Y1). At
 * more, each candidate's note is its combined note - `score` the mean, the rest of
 * the note that of the reading that carries it, `reading` its number - and
 * `readings` keeps every reading whole, the failed ones too. The style is `absent`
 * when every reading failed, and still keeps its readings (§7.5).
 */
export function combineStyleReadings(
  readings: readonly AnalysisStyleReading[],
  styleRuns: number,
): CombinedStyle {
  const ordered = [...readings].sort((a, b) => a.run - b.run);
  const several = styleRuns > 1;
  const kept = several ? ordered : [];
  const rendered = ordered.flatMap((reading) => (reading.state === 'rendered' ? [reading] : []));
  const first = rendered[0];
  if (first === undefined) return { state: 'absent', readings: kept };
  if (!several) return { state: 'rendered', notes: first.notes, readings: [] };

  const notes = NOYAU_CODES.flatMap((noyau): AnalysisStyleNote[] => {
    const given = rendered.flatMap((reading) => {
      const note = reading.notes.find((entry) => entry.noyau === noyau);
      return note === undefined ? [] : [{ run: reading.run, note }];
    });
    const combined = combinedStyleScore(given.map(({ run, note }) => ({ run, score: note.score })));
    const carrier = given.find((entry) => entry.run === combined?.reading);
    if (combined === null || carrier === undefined) return [];
    return [{ ...carrier.note, score: combined.score, reading: combined.reading }];
  });
  return { state: 'rendered', notes, readings: kept };
}

/* --------------------------------------------------- what an analysis says of its readings */

/**
 * How many readings an analysis asked for, how many were recorded, rendered and
 * failed - what « 3 lectures » and « 2 lectures sur 3 » are said from. `partial` is
 * true when the style was read several times and some, not all, of the readings
 * rendered.
 */
export type StyleReadingsSummary = {
  readonly requested: number;
  readonly rendered: number;
  readonly failed: number;
  readonly partial: boolean;
};

/**
 * The summary of the readings of an analysis, or null when the style was read once
 * (the default: `readings` is empty, §10) or not at all.
 */
export function styleReadingsOf(analysis: NoyauAnalysis | null): StyleReadingsSummary | null {
  const readings = analysis?.style?.readings ?? [];
  if (readings.length === 0) return null;
  const rendered = readings.filter((reading) => reading.state === 'rendered').length;
  return {
    requested: readings.length,
    rendered,
    failed: readings.length - rendered,
    partial: rendered > 0 && rendered < readings.length,
  };
}

/** « 1 lecture » or « 3 lectures ». */
function readingsPhrase(count: number): string {
  return count === 1 ? '1 lecture' : `${count} lectures`;
}

/**
 * The words the parcours, the markdown and the assistant say the readings in (§11.1,
 * Y10): « 3 lectures » when every reading rendered, « 2 lectures sur 3 » when only a
 * part did. Null when the style was read once, and when every reading failed - the
 * style is then absent and says so.
 */
export function styleReadingsLabel(summary: StyleReadingsSummary | null): string | null {
  if (summary === null || summary.rendered === 0) return null;
  if (!summary.partial) return readingsPhrase(summary.requested);
  return `${readingsPhrase(summary.rendered)} sur ${summary.requested}`;
}

/* --------------------------------------------------------------- the subsampling */

/** One place of a subsample: the noyau and its note. */
export type StyleSubsamplePlace = { readonly noyau: NoyauCode; readonly score: number };

/**
 * What a trial would have said with a part of its readings (§11.4, Y9): the principal
 * and the second the code would have given, with their notes.
 *
 * `tie` says a tie at the head: the noyaux that share the note that decides the
 * principal (`place: 'principal'`) or the second (`place: 'second'`) - nothing was
 * drawn, so `principal` and `second` list those noyaux in the order of the table and
 * the tie is the fact. `style` is `absent` when every reading of the subsample
 * failed: the notes are then the compatibility alone.
 */
export type StyleSubsample = {
  readonly runs: readonly number[];
  readonly style: 'rendered' | 'absent';
  readonly principal: StyleSubsamplePlace;
  readonly second: StyleSubsamplePlace;
  readonly tie: {
    readonly place: 'principal' | 'second';
    readonly noyaux: readonly NoyauCode[];
  } | null;
};

/** The subsamples of one size: every combination of `size` readings among those of the trial. */
export type StyleSubsampleGroup = {
  readonly size: number;
  readonly samples: readonly StyleSubsample[];
};

/** Every combination of `size` items among `items`, in order. */
function combinationsOf<T>(items: readonly T[], size: number): T[][] {
  if (size === 0) return [[]];
  return items.flatMap((item, index) =>
    combinationsOf(items.slice(index + 1), size - 1).map((rest) => [item, ...rest]),
  );
}

/**
 * What the readings of an analysis would have rendered with FEWER of them (§11.4, L11,
 * L12, Y9): for each k from 1 to N - 1, and for EVERY combination of k readings among
 * the N, the principal and the second the code would have given, with their notes - a
 * reading that failed counted as failed, a combination of failed readings the style
 * absent, a tie at the head said as such and never drawn.
 *
 * Computed on read from the analysis alone, never stored: the compatibility, the
 * candidates, the weight and the readings are all in it. Empty when the style was
 * read once or not at all, and when the analysis holds no six notes.
 */
export function styleSubsamplesOf(analysis: NoyauAnalysis | null): StyleSubsampleGroup[] {
  if (analysis === null || analysis.noyaux.length < 2) return [];
  const readings = [...(analysis.style?.readings ?? [])].sort((a, b) => a.run - b.run);
  if (readings.length < 2) return [];
  const rows = analysis.noyaux;

  const sampled = (chosen: readonly AnalysisStyleReading[]): StyleSubsample => {
    const combined = combineStyleReadings(chosen, 2);
    const styleOf = new Map(
      (combined.state === 'rendered' ? combined.notes : []).map((note) => [note.noyau, note.score]),
    );
    const candidateScores = rows.flatMap((row) =>
      row.candidate
        ? [
            {
              noyau: row.noyau,
              score: candidateNote(
                row.compatibility,
                styleOf.get(row.noyau) ?? null,
                analysis.settings.styleWeight,
              ),
            },
          ]
        : [],
    );
    const lowest =
      candidateScores.length === 0 ? null : Math.min(...candidateScores.map((row) => row.score));
    const scoreOf = new Map(candidateScores.map((row) => [row.noyau, row.score]));
    const ranked = rankAnalyzedNoyaux(
      rows.map((row) => ({
        noyau: row.noyau,
        score: scoreOf.get(row.noyau) ?? eliminatedNote(row.compatibility, lowest),
        eliminated: !row.candidate,
      })),
      NOYAU_CODES,
    );
    const place = (noyau: NoyauCode): StyleSubsamplePlace => ({
      noyau,
      score:
        scoreOf.get(noyau) ??
        eliminatedNote(rows.find((row) => row.noyau === noyau)?.compatibility ?? 0, lowest),
    });
    return {
      runs: chosen.map((reading) => reading.run),
      style: combined.state,
      principal: place(ranked.principal),
      second: place(ranked.second),
      tie: ranked.draw === null ? null : { place: ranked.draw.place, noyaux: ranked.draw.tied },
    };
  };

  const groups: StyleSubsampleGroup[] = [];
  for (let size = 1; size < readings.length; size += 1) {
    groups.push({ size, samples: combinationsOf(readings, size).map(sampled) });
  }
  return groups;
}
